/**
 * Multi-country center-config hooks.
 *
 * useCurrency(centerCode) → { code, symbol, locale, format }
 *
 * For delivery channels, use `useActiveChannels(session, center)` from
 * `./useActiveChannels` — that hook already exists and handles the auth +
 * legacy-key coercion. This file only owns the currency cache.
 */
import { useEffect, useState } from 'react';
import { resolveCurrency, formatMoney, makeFormatter } from '../utils/money';

const API = process.env.REACT_APP_BACKEND_URL;

const _currencyCache = new Map();   // center → meta {code,symbol,locale}
const _currencyInflight = new Map();

async function _fetchCurrency(center) {
  const cached = _currencyCache.get(center);
  if (cached) return cached;
  const infl = _currencyInflight.get(center);
  if (infl) return infl;
  const p = (async () => {
    try {
      const res = await fetch(`${API}/api/config/currency/${encodeURIComponent(center)}`);
      if (!res.ok) throw new Error('bad status');
      const d = await res.json();
      const meta = resolveCurrency({ currency: d.code, country: d.country });
      _currencyCache.set(center, meta);
      return meta;
    } catch {
      // Fallback — infer from center code prefix (best-effort so screens never break)
      const meta = /PERTH|-AU|-US|-UK/i.test(center) ? resolveCurrency(center.replace(/.*-/, '')) : resolveCurrency('INR');
      _currencyCache.set(center, meta);
      return meta;
    } finally {
      _currencyInflight.delete(center);
    }
  })();
  _currencyInflight.set(center, p);
  return p;
}

/**
 * Return the resolved currency meta for `centerCode`. Initial render returns
 * the safe INR default, then re-renders once the real value arrives.
 * Also exposes a `format(value, digits, sign)` closure ready to use.
 */
export function useCurrency(centerCode) {
  const [meta, setMeta] = useState(() => _currencyCache.get(centerCode) || resolveCurrency('INR'));
  useEffect(() => {
    if (!centerCode) { setMeta(resolveCurrency('INR')); return; }
    let alive = true;
    _fetchCurrency(centerCode).then((m) => { if (alive) setMeta(m); });
    return () => { alive = false; };
  }, [centerCode]);
  return {
    ...meta,
    format: (value, digits = 0, sign = false) => formatMoney(value, meta, { digits, sign }),
    make: (digits = 0, sign = false) => (v) => formatMoney(v, meta, { digits, sign }),
  };
}

/** Same as useCurrency but expects a full center *doc*: `{currency, country, is_india_center}`. */
export function useCurrencyFromDoc(doc) {
  const meta = resolveCurrency(doc || {});
  return { ...meta, format: makeFormatter(meta), make: (d = 0, s = false) => (v) => formatMoney(v, meta, { digits: d, sign: s }) };
}

/** Invalidate the currency cache — call after admin edits to Center Config. */
export function refreshCurrencyCache(center) {
  if (center) _currencyCache.delete(center); else _currencyCache.clear();
}
